import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import Nav from "../components/Nav";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { StyleTag } from "./Signup";
import { TrendingUp, Users, MousePointerClick, RefreshCw } from "lucide-react";

export default function Admin() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch (e) {
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (user && !user.is_admin) return <Navigate to="/chat" replace />;

  const t = stats?.totals || { visits: 0, signups: 0, conversion: 0 };

  return (
    <div className="min-h-screen bg-[#F4F7F6]">
      <Nav />
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-4xl text-slate-800">Traffic & sign-ups</h1>
            <p className="text-slate-600 mt-1">Where people came from, and who joined. First-touch attribution.</p>
          </div>
          <button
            onClick={load}
            data-testid="admin-refresh"
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-[#2E9D96] border border-slate-200 rounded-full px-4 py-2 transition-colors bg-white"
          >
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {/* Totals */}
        <div className="mt-8 grid sm:grid-cols-3 gap-4" data-testid="admin-totals">
          <StatCard icon={<MousePointerClick size={18} />} label="Unique visitors" value={t.visits} />
          <StatCard icon={<Users size={18} />} label="Sign-ups" value={t.signups} />
          <StatCard icon={<TrendingUp size={18} />} label="Conversion" value={`${t.conversion}%`} accent />
        </div>

        {/* By source */}
        <Panel title="By source" testid="admin-by-source" hint="Tag ads with ?utm_source=facebook">
          <Table
            rows={stats?.by_source}
            firstKey="source"
            firstLabel="Source"
            empty="No traffic yet — share a UTM link to start seeing sources."
          />
        </Panel>

        {/* By campaign */}
        <Panel title="By campaign" testid="admin-by-campaign" hint="Tag ads with &utm_campaign=weekend-dads">
          <Table rows={stats?.by_campaign} firstKey="campaign" firstLabel="Campaign" empty="No campaigns tagged yet." />
        </Panel>

        {/* Daily */}
        <Panel title="Day by day" testid="admin-daily">
          {stats?.daily?.length ? (
            <div className="space-y-2">
              {stats.daily.map((d) => (
                <div key={d.date} className="flex items-center justify-between text-sm py-2 border-b border-slate-100 last:border-0">
                  <span className="font-semibold text-slate-700">{d.date}</span>
                  <span className="text-slate-500">
                    {d.visits} visits · <span className="text-[#2E9D96] font-semibold">{d.signups} joined</span>
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-sm">Nothing yet.</p>
          )}
        </Panel>

        <div className="mt-8 bg-white rounded-3xl p-6 border border-slate-100 shadow-sm">
          <p className="text-sm font-semibold text-slate-700">How to tag your ads</p>
          <p className="text-sm text-slate-500 mt-2 leading-relaxed">
            Add UTM tags to the link in each ad. Example:
          </p>
          <code className="block mt-2 text-[13px] bg-[#F4F7F6] border border-slate-200 rounded-xl px-4 py-3 text-slate-700 break-all">
            https://your-domain.com/?utm_source=facebook&utm_medium=paid&utm_campaign=weekend-dads
          </code>
          <p className="text-sm text-slate-500 mt-3 leading-relaxed">
            Change <span className="font-semibold text-slate-700">utm_source</span> per platform (facebook, instagram, tiktok…) and{" "}
            <span className="font-semibold text-slate-700">utm_campaign</span> per ad so you can see exactly which one pulls best.
          </p>
        </div>
      </div>
      <StyleTag />
    </div>
  );
}

function StatCard({ icon, label, value, accent }) {
  return (
    <div className={`rounded-3xl p-6 border shadow-sm ${accent ? "bg-[#2E9D96] border-[#2E9D96] text-white" : "bg-white border-slate-100"}`}>
      <div className={`w-10 h-10 rounded-2xl flex items-center justify-center mb-4 ${accent ? "bg-white/20 text-white" : "bg-[#CDE7E3] text-slate-700"}`}>
        {icon}
      </div>
      <p className={`text-xs uppercase tracking-[0.15em] font-semibold ${accent ? "text-white/80" : "text-slate-500"}`}>{label}</p>
      <p className={`font-display text-4xl mt-1 ${accent ? "text-white" : "text-slate-800"}`}>{value}</p>
    </div>
  );
}

function Panel({ title, hint, testid, children }) {
  return (
    <section className="mt-6 bg-white rounded-3xl p-8 border border-slate-100 shadow-sm" data-testid={testid}>
      <div className="flex items-baseline justify-between gap-3 mb-5">
        <h2 className="font-display text-2xl text-slate-800">{title}</h2>
        {hint && <span className="text-xs text-slate-400">{hint}</span>}
      </div>
      {children}
    </section>
  );
}

function Table({ rows, firstKey, firstLabel, empty }) {
  if (!rows || rows.length === 0) return <p className="text-slate-500 text-sm">{empty}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-[0.12em] text-slate-400 border-b border-slate-100">
            <th className="py-2 pr-4 font-semibold">{firstLabel}</th>
            <th className="py-2 px-4 font-semibold text-right">Visitors</th>
            <th className="py-2 px-4 font-semibold text-right">Joined</th>
            <th className="py-2 pl-4 font-semibold text-right">Conv.</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-50 last:border-0">
              <td className="py-3 pr-4 font-semibold text-slate-700">{r[firstKey]}</td>
              <td className="py-3 px-4 text-right text-slate-600">{r.visits}</td>
              <td className="py-3 px-4 text-right text-slate-600">{r.signups}</td>
              <td className="py-3 pl-4 text-right font-semibold text-[#2E9D96]">{r.conversion}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
